/*************************************************/
/* ACCORDION                                     */
/*************************************************/

export function accordion() {
    let resizeTimer;
    const accordionComponentSelector = '.accordion-component';
    const accordionParentSelector = '.accordion';
    const accordionItemSelector = '.accordion__item';
    const accordionItemToggleSelector = '.accordion__item-head';
    const accordionItemContentSelector = '.accordion__item-content';
    const accordionItemActiveClass = 'accordion__item--active';
    const accordionIdDataAttributeName = 'data-accordion-id';
    const accordionItemIdDataAttributeName = 'data-accordion-item-id';
    const accordionActivateItemEventName = 'activateItem';
    const accordionDeactivateItemEventName = 'deactivateItem';
    const accordionDeactivateAllItemsEventName = 'deactivateAllItems';
    const accordionTabControlSelector = '.accordion-component__tab';

    const getTargetedAccordion = (event) => {
        if (event && event.detail && event.detail.accordionId) {
            return document.querySelector(
                `[${accordionIdDataAttributeName}="${event.detail.accordionId}"]`
            );
        }
        return null;
    };

    const getTargetedAccordionItem = (event) => {
        if (event != null) {
            const targetedAccordion = getTargetedAccordion(event);
            if (targetedAccordion != null) {
                return targetedAccordion.querySelector(
                    `[${accordionItemIdDataAttributeName}="${event.detail.accordionItemId}"]`
                );
            }
        }
        return null;
    };

    const activateAccordion = (tab, accordion) => {
        if (accordion && tab) {
            const accordionComponent = accordion.closest(accordionComponentSelector);
            if (!accordionComponent) return;

            const accordionSiblings = accordionComponent.querySelectorAll(accordionParentSelector);
            accordionSiblings.forEach(sibling => {
                sibling.setAttribute('hidden', 'hidden');
            });

            const tabSiblings = accordionComponent.querySelectorAll(accordionTabControlSelector);
            tabSiblings.forEach(sibling => {
                sibling.setAttribute('aria-selected', 'false');
            });

            accordion.removeAttribute('hidden');
            tab.setAttribute('aria-selected', 'true');
        }
    };

    const setActiveStylesOnAccordionItem = (accordionItem) => {
        if (accordionItem != null) {
            const accordionItemContent = accordionItem.querySelector(accordionItemContentSelector);
            if (accordionItemContent) {
                accordionItemContent.style.maxHeight = `${accordionItemContent.scrollHeight}px`;
                accordionItemContent.setAttribute('aria-hidden', false);
                accordionItem.classList.add(accordionItemActiveClass);
                accordionItem.setAttribute('aria-expanded', true);
            }
        }
    };

    const setInactiveStylesOnAccordionItem = (accordionItem) => {
        if (accordionItem != null) {
            const accordionItemContent = accordionItem.querySelector(accordionItemContentSelector);
            if (accordionItemContent) {
                accordionItemContent.style.maxHeight = "0px";
                accordionItemContent.setAttribute('aria-hidden', true);
                accordionItem.classList.remove(accordionItemActiveClass);
                accordionItem.setAttribute('aria-expanded', false);
            }
        }
    };

    const init = () => {
        const accordions = document.querySelectorAll(accordionParentSelector);
        const accordionTabs = document.querySelectorAll(accordionTabControlSelector);

        // Core fix: Only require accordions to exist, not tabs
        if (accordions.length > 0) {

            // Reset maxHeight's on active items if screen width changes
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(function () {
                    const activeAccordionItems = document.querySelectorAll('.' + accordionItemActiveClass);
                    activeAccordionItems.forEach(accordionItem => {
                        const content = accordionItem.querySelector(accordionItemContentSelector);
                        if (content) {
                            content.style.maxHeight = `${content.scrollHeight}px`;
                        }
                    });
                }, 50);
            });

            accordions.forEach(accordion => {
                // Event: Activate Item
                accordion.addEventListener(accordionActivateItemEventName, e => {
                    const targetedAccordionItem = getTargetedAccordionItem(e);
                    if (e.detail.deactivateAllItems) {
                        const deactivateAllItemsEvent = new CustomEvent(accordionDeactivateAllItemsEventName, {
                            detail: { accordionId: e.detail.accordionId }
                        });
                        accordion.dispatchEvent(deactivateAllItemsEvent);
                    }

                    if (targetedAccordionItem != null) {
                        setActiveStylesOnAccordionItem(targetedAccordionItem);
                    }
                });

                // Event: Deactivate Item
                accordion.addEventListener(accordionDeactivateItemEventName, e => {
                    const targetedAccordionItem = getTargetedAccordionItem(e);
                    if (targetedAccordionItem != null) {
                        setInactiveStylesOnAccordionItem(targetedAccordionItem);
                    }
                });

                // Event: Deactivate All
                accordion.addEventListener(accordionDeactivateAllItemsEventName, e => {
                    const targetedAccordion = getTargetedAccordion(e);
                    if (targetedAccordion != null) {
                        targetedAccordion.querySelectorAll(accordionItemSelector).forEach(item => {
                            setInactiveStylesOnAccordionItem(item);
                        });
                    }
                });

                // Click Listeners for individual items
                accordion.querySelectorAll(accordionItemToggleSelector).forEach(toggle => {
                    toggle.addEventListener('click', e => {
                        const clickedAccordionItem = e.target.closest(accordionItemSelector);
                        const itemId = clickedAccordionItem.dataset.accordionItemId;
                        const accordionParent = e.target.closest(accordionParentSelector);
                        const accordionId = accordionParent.dataset.accordionId;

                        if (clickedAccordionItem.classList.contains(accordionItemActiveClass)) {
                            const deactiveEvent = new CustomEvent(accordionDeactivateItemEventName, {
                                detail: { accordionId: accordionId, accordionItemId: itemId }
                            });
                            accordionParent.dispatchEvent(deactiveEvent);
                        } else {
                            const activateEvent = new CustomEvent(accordionActivateItemEventName, {
                                detail: {
                                    accordionId: accordionId,
                                    accordionItemId: itemId,
                                    deactivateAllItems: true
                                }
                            });
                            accordionParent.dispatchEvent(activateEvent);
                        }
                    });
                });
            });

            // Handle tabs separately (Optional)
            if (accordionTabs.length > 0) {
                accordionTabs.forEach(tab => {
                    tab.addEventListener('click', () => {
                        const targetAccordion = document.getElementById(tab.getAttribute('aria-controls'));
                        if (targetAccordion) {
                            activateAccordion(tab, targetAccordion);
                        }
                    });
                });
            }
        }
    };

    init();
}